import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type {
	PublicCourseData,
	Course,
	CourseTreeNode,
	TopicNode,
	LessonNode,
} from "@/models/interfaces";
import { getPublicCourseData, enrollInPublicCourse, getCourse } from "@/api/courses";
import {
	getCourseTopLevelNodes,
	getCourseNode,
	getNodeChildren,
} from "@/api/course_tree";

interface CourseContentCache {
	[courseId: string]: {
		topics: Array<TopicNode & { lessons: LessonNode[] }>;
		lastUpdated: number;
	};
}

export const usePublicCourseStore = defineStore("publicCourse", () => {
	// State
	const publicCourseData = ref<PublicCourseData | null>(null);
	const enrolledCourse = ref<Course | null>(null);
	const courseContentCache = ref<CourseContentCache>({});
	const currentSlug = ref<string | null>(null);
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Course content state
	const currentCourseContent = ref<Array<TopicNode & { lessons: LessonNode[] }>>([]);

	// Getters
	const isPublicCourse = computed(() => publicCourseData.value !== null);

	const courseMetadata = computed(() => {
		if (publicCourseData.value) {
			return publicCourseData.value.course.public_metadata;
		}
		return enrolledCourse.value?.public_metadata || null;
	});

	const courseStats = computed(() => publicCourseData.value?.stats || null);

	const instructor = computed(() => publicCourseData.value?.instructor || null);

	const courseId = computed(() => {
		return publicCourseData.value?.course.id || enrolledCourse.value?.id;
	});

	const courseName = computed(() => {
		return publicCourseData.value?.course.name || enrolledCourse.value?.name;
	});

	const isUserEnrolled = computed(() => {
		return enrolledCourse.value?.enrolled === true;
	});

	// Actions
	const loadPublicCourse = async (slug: string) => {
		loading.value = true;
		error.value = null;
		currentSlug.value = slug;

		try {
			const data = await getPublicCourseData(slug);
			publicCourseData.value = data;
			return data;
		} catch (err: any) {
			error.value = err.response?.data?.detail || "Course not found";
			throw err;
		} finally {
			loading.value = false;
		}
	};

	const enrollInCourse = async (redirectPath?: string) => {
		if (!currentSlug.value) {
			throw new Error("No course slug available for enrollment");
		}

		try {
			await enrollInPublicCourse(currentSlug.value, redirectPath);

			// After enrollment, load the enrolled course data
			if (publicCourseData.value) {
				enrolledCourse.value = await getCourse(publicCourseData.value.course.id);
			}

			return true;
		} catch (err: any) {
			error.value = err.message || "Enrollment failed";
			throw err;
		}
	};

	const loadEnrolledCourse = async (courseIdOrSlug: string) => {
		loading.value = true;
		error.value = null;

		try {
			let course: Course;

			// If it's a slug (no UUID format), get course via public API
			if (!courseIdOrSlug.includes("-") || courseIdOrSlug.length < 20) {
				currentSlug.value = courseIdOrSlug;
				const publicData = await getPublicCourseData(courseIdOrSlug);
				course = publicData.course;
			} else {
				// Direct course ID lookup
				course = await getCourse(courseIdOrSlug);
			}

			enrolledCourse.value = course;
			return course;
		} catch (err: any) {
			error.value = err.response?.data?.detail || "Course not found";
			throw err;
		} finally {
			loading.value = false;
		}
	};

	const loadCourseContent = async (courseIdParam: string, forceRefresh = false) => {
		const id = courseIdParam || courseId.value;
		if (!id) throw new Error("No course ID available");

		// Check cache first (cache for 5 minutes)
		const cached = courseContentCache.value[id];
		const now = Date.now();
		if (cached && !forceRefresh && now - cached.lastUpdated < 5 * 60 * 1000) {
			currentCourseContent.value = cached.topics;
			return cached.topics;
		}

		try {
			// Load top-level nodes (topics)
			const topLevelResponse = await getCourseTopLevelNodes(id, 1);
			const topics = topLevelResponse.data.filter(
				node => node.resourcetype === "TopicNode",
			) as TopicNode[];

			// Load lessons for each topic
			const topicsWithLessons = await Promise.all(
				topics.map(async topic => {
					try {
						const childrenResponse = await getNodeChildren(id, topic.id, 1);
						const lessons = childrenResponse.data.filter(
							node =>
								node.resourcetype === "LessonNode" || node.resourcetype === "FileNode",
						) as LessonNode[];

						// Sort lessons by order_index if available
						lessons.sort((a, b) => {
							const orderA = a.order_index ?? 999;
							const orderB = b.order_index ?? 999;
							return orderA - orderB;
						});

						return {
							...topic,
							lessons,
						};
					} catch (err) {
						console.warn(`Failed to load lessons for topic ${topic.id}:`, err);
						return {
							...topic,
							lessons: [],
						};
					}
				}),
			);

			// Sort topics by order_index if available
			topicsWithLessons.sort((a, b) => {
				const orderA = a.order_index ?? 999;
				const orderB = b.order_index ?? 999;
				return orderA - orderB;
			});

			// Update cache
			courseContentCache.value[id] = {
				topics: topicsWithLessons,
				lastUpdated: now,
			};

			currentCourseContent.value = topicsWithLessons;
			return topicsWithLessons;
		} catch (err: any) {
			error.value = err.message || "Failed to load course content";
			throw err;
		}
	};

	const getLessonById = async (
		courseIdParam: string,
		lessonId: string,
	): Promise<CourseTreeNode | null> => {
		const id = courseIdParam || courseId.value;
		if (!id) return null;

		try {
			const lesson = await getCourseNode(id, lessonId);
			return lesson;
		} catch (err) {
			console.error("Failed to load lesson:", err);
			return null;
		}
	};

	const getSlugFromUrl = (url: string): string | null => {
		const match = url.match(/\/c\/([^/]+)/);
		return match ? match[1] : null;
	};

	const generateSlugFromCourseName = (courseName: string): string => {
		return courseName
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, "")
			.replace(/\s+/g, "-")
			.replace(/-+/g, "-")
			.trim();
	};

	const formatDuration = (minutes: number): string => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;

		if (hours > 0) {
			return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
		}
		return `${mins}m`;
	};

	const reset = () => {
		publicCourseData.value = null;
		enrolledCourse.value = null;
		currentCourseContent.value = [];
		currentSlug.value = null;
		loading.value = false;
		error.value = null;
	};

	const clearError = () => {
		error.value = null;
	};

	return {
		// State
		publicCourseData,
		enrolledCourse,
		currentCourseContent,
		currentSlug,
		loading,
		error,

		// Getters
		isPublicCourse,
		courseMetadata,
		courseStats,
		instructor,
		courseId,
		courseName,
		isUserEnrolled,

		// Actions
		loadPublicCourse,
		enrollInCourse,
		loadEnrolledCourse,
		loadCourseContent,
		getLessonById,
		getSlugFromUrl,
		generateSlugFromCourseName,
		formatDuration,
		reset,
		clearError,
	};
});
